import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, BellOff, CheckCircle2, Globe, SlidersHorizontal } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import Switch from '../../../components/ui/Switch';
import Button from '../../../components/ui/Button';
import { cn } from '@/utils/cn';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { apiErrorsOf } from '@/utils/requestHandling';
import {
  AcceptanceNoticeMode,
  ExpiryNoticeMode,
  HrSummaryMode,
  TournamentSettingsErrorCode,
  getTournamentSettings,
  updateTournamentSettings,
} from '@/api/TournamentManagement/tournamentSettingsApi';
import type { TournamentSettingsInput, TournamentSettingsView } from '@/api/TournamentManagement/tournamentSettingsApi';

// CO-843: the company's tournament notice settings, for Hr and Admin. Four settings, one save
// (StickyActionBar). Every hour is a whole hour in the company time zone. Quiet hours hold HR's
// notices only; a notice type switched off on Notification Management stays off whatever is chosen
// here, and the page says so. Match durations are NOT here: they stay on Work Points.

/** How long the "saved" note stays up: the same time the app's own success toast stays (hooks/useToast.ts). */
const SAVED_NOTE_MS = 6000;

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

/** Server refusal codes this page can explain in its own words. */
const ERROR_KEYS: Record<string, string> = {
  [TournamentSettingsErrorCode.QuietHoursSameStartAndEnd]: 'tournamentSettings.error.sameHours',
  [TournamentSettingsErrorCode.SummaryTimesRequired]: 'tournamentSettings.error.timesRequired',
  [TournamentSettingsErrorCode.HourOutOfRange]: 'tournamentSettings.error.hourOutOfRange',
  [TournamentSettingsErrorCode.UnknownOption]: 'tournamentSettings.error.unknownOption',
};

/** Fixed key order, so two forms can be compared as text. */
function toForm(view: TournamentSettingsView): TournamentSettingsInput {
  return {
    quietHoursEnabled: Boolean(view.quietHoursEnabled),
    quietHoursStartHour: view.quietHoursStartHour,
    quietHoursEndHour: view.quietHoursEndHour,
    hrSummaryMode: view.hrSummaryMode,
    hrSummaryTimes: [...(view.hrSummaryTimes ?? [])].sort((a, b) => a - b),
    acceptanceNoticeMode: view.acceptanceNoticeMode,
    expiryNoticeMode: view.expiryNoticeMode,
  };
}

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

interface SettingsSectionProps {
  title: string;
  description: string;
  testId: string;
  /** Receives the id of the section heading, to name the controls after it. */
  children: (headingId: string) => React.ReactNode;
}

/** One row of the page: what the setting is on one side, its controls on the other. */
const SettingsSection: React.FC<SettingsSectionProps> = ({ title, description, testId, children }) => {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      data-testid={testId}
      className="grid gap-4 px-5 py-6 sm:px-6 md:grid-cols-[minmax(0,5fr)_minmax(0,8fr)] md:gap-10"
    >
      <div>
        <h2 id={headingId} className="text-base font-semibold text-foreground">{title}</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="min-w-0 space-y-4">{children(headingId)}</div>
    </section>
  );
};

interface ChoiceOption {
  value: number;
  title: string;
  description: string;
}

interface ChoiceListProps {
  name: string;
  labelledBy: string;
  value: number;
  options: ChoiceOption[];
  onChange: (value: number) => void;
}

/** Native radios (arrow keys and form semantics for free) drawn as rows with a visible selected state. */
const ChoiceList: React.FC<ChoiceListProps> = ({ name, labelledBy, value, options, onChange }) => (
  <div role="radiogroup" aria-labelledby={labelledBy} className="space-y-2">
    {options.map((option) => {
      const checked = option.value === value;
      return (
        <label
          key={option.value}
          data-testid={`${name}-option-${option.value}`}
          data-selected={checked ? 'true' : 'false'}
          className={cn(
            // `relative` is load-bearing: it is the positioned ancestor of the visually hidden radio.
            'relative flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors duration-200 motion-reduce:transition-none',
            checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
          )}
        >
          <input
            type="radio"
            className="peer sr-only"
            name={name}
            value={option.value}
            checked={checked}
            onChange={() => onChange(option.value)}
          />
          <span
            aria-hidden="true"
            className={cn(
              'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
              checked ? 'border-primary' : 'border-muted-foreground',
            )}
          >
            {checked && <span className="h-2 w-2 rounded-full bg-primary" />}
          </span>
          <span className="min-w-0">
            <span className={cn('block text-sm font-medium', checked ? 'text-primary' : 'text-foreground')}>{option.title}</span>
            <span className="mt-0.5 block text-sm text-muted-foreground">{option.description}</span>
          </span>
        </label>
      );
    })}
  </div>
);

interface HourSelectProps {
  label: string;
  value: number;
  onChange: (hour: number) => void;
  invalid: boolean;
  describedBy?: string;
  testId: string;
}

const HourSelect: React.FC<HourSelectProps> = ({ label, value, onChange, invalid, describedBy, testId }) => {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium text-foreground">{label}</label>
      <select
        id={id}
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={cn('h-11 w-full rounded-lg border bg-background px-3 text-sm tabular-nums text-foreground', FOCUS_RING, invalid ? 'border-red-500' : 'border-border')}
      >
        {HOURS.map((hour) => <option key={hour} value={hour}>{hourLabel(hour)}</option>)}
      </select>
    </div>
  );
};

const FieldError: React.FC<{ id?: string; testId: string; children: React.ReactNode }> = ({ id, testId, children }) => (
  <p id={id} role="alert" data-testid={testId} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
    <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
    <span>{children}</span>
  </p>
);

const TournamentSettings: React.FC = () => {
  const { t } = useTranslation('tournament');
  const [form, setForm] = useState<TournamentSettingsInput | null>(null);
  const [saved, setSaved] = useState<TournamentSettingsInput | null>(null);
  const [zone, setZone] = useState<{ id: string | null; isFallback: boolean }>({ id: null, isFallback: false });
  const [loading, setLoading] = useState(true);
  // Failures are kept raw and worded at render, so a language switch rewords them without a reload.
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [saveFailure, setSaveFailure] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  // The save confirmation is drawn by this page. react-hot-toast is NOT an option here: its
  // <Toaster/> is mounted on four other pages only, so a toast fired from this one never renders.
  const [justSaved, setJustSaved] = useState(false);
  const windowErrorId = useId();
  const timesLabelId = useId();

  const apply = useCallback((view: TournamentSettingsView) => {
    const next = toForm(view);
    setSaved(next);
    setForm(next);
    setZone({ id: view.companyTimeZone, isFallback: Boolean(view.companyTimeZoneIsFallback) });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailure(null);
    try {
      const result = await getTournamentSettings();
      if (result.errors.length > 0 || !result.payload) setLoadFailure(result);
      else apply(result.payload);
    } catch (error) {
      setLoadFailure(error);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { void load(); }, [load]);

  // The note leaves on its own, or sooner: on the next edit and on the next save.
  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = window.setTimeout(() => setJustSaved(false), SAVED_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [justSaved]);

  const patch = (changes: Partial<TournamentSettingsInput>) => {
    setSaveFailure(null);
    setJustSaved(false);
    setForm((current) => (current ? { ...current, ...changes } : current));
  };

  // The stored window must always be a real one, even while quiet hours are off, and the two hour
  // pickers are hidden then: switching off with equal hours puts the saved window back.
  const setQuietHoursEnabled = (enabled: boolean) => {
    if (!form || !saved) return;
    if (enabled || form.quietHoursStartHour !== form.quietHoursEndHour) patch({ quietHoursEnabled: enabled });
    else patch({ quietHoursEnabled: false, quietHoursStartHour: saved.quietHoursStartHour, quietHoursEndHour: saved.quietHoursEndHour });
  };

  const toggleSummaryHour = (hour: number) => {
    if (!form) return;
    const times = form.hrSummaryTimes.includes(hour)
      ? form.hrSummaryTimes.filter((value) => value !== hour)
      : [...form.hrSummaryTimes, hour].sort((a, b) => a - b);
    patch({ hrSummaryTimes: times });
  };

  const sameHours = form !== null && form.quietHoursStartHour === form.quietHoursEndHour;
  const timesMissing = form !== null && form.hrSummaryMode === HrSummaryMode.AtTimes && form.hrSummaryTimes.length === 0;
  const invalid = sameHours || timesMissing;
  const hasUnsavedChanges = form !== null && saved !== null && JSON.stringify(form) !== JSON.stringify(saved);

  const handleSave = async () => {
    if (!form) return;
    setAttempted(true);
    if (invalid) return;
    setSaving(true);
    setSaveFailure(null);
    setJustSaved(false);
    try {
      const result = await updateTournamentSettings(form);
      if (result.errors.length > 0 || !result.payload) {
        setSaveFailure(result);
        return;
      }
      apply(result.payload);
      setAttempted(false);
      setJustSaved(true);
    } catch (error) {
      setSaveFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(saved);
    setAttempted(false);
    setSaveFailure(null);
  };

  const saveFailureMessage = (): string => {
    const known = apiErrorsOf(saveFailure).map((error) => ERROR_KEYS[error.code ?? '']).find(Boolean);
    return known ? t(known) : getApiErrorMessage(saveFailure, t, t('tournamentSettings.error.saveFailed'));
  };

  const pageAlert = attempted && invalid
    ? t('tournamentSettings.error.fixBeforeSaving')
    : saveFailure ? saveFailureMessage() : null;

  return (
    <div data-testid="tournament-settings-page">
      <PageLayout
        title={t('Tournament Settings')}
        description={t('tournamentSettings.description')}
        icon={SlidersHorizontal}
        accentColor="amber"
        maxWidth="5xl"
      >
        {loading && (
          <div role="status" aria-label={t('tournamentSettings.loading')} data-testid="tournament-settings-loading" className="divide-y divide-border rounded-lg border border-border bg-card">
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="grid animate-pulse gap-4 px-5 py-6 motion-reduce:animate-none sm:px-6 md:grid-cols-[minmax(0,5fr)_minmax(0,8fr)] md:gap-10">
                <div className="space-y-2">
                  <div className="h-4 w-40 rounded bg-muted" />
                  <div className="h-3 w-full max-w-xs rounded bg-muted" />
                </div>
                <div className="space-y-2">
                  <div className="h-11 rounded-lg bg-muted" />
                  <div className="h-11 rounded-lg bg-muted" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && loadFailure !== null && (
          <div role="alert" data-testid="tournament-settings-load-error" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <AlertCircle aria-hidden="true" className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
            <span className="min-w-0 flex-1 text-sm text-red-700 dark:text-red-300">{getApiErrorMessage(loadFailure, t, t('tournamentSettings.error.loadFailed'))}</span>
            <Button variant="outline" size="sm" onClick={() => { void load(); }} data-testid="tournament-settings-retry">{t('Try Again')}</Button>
          </div>
        )}

        {!loading && loadFailure === null && form && (
          // `relative` is load-bearing: positioned ancestor for the visually hidden radios below.
          <div className="relative">
            <div className="divide-y divide-border rounded-lg border border-border bg-card">
              <div className="space-y-3 px-5 py-4 text-sm sm:px-6" data-testid="tournament-settings-context">
                {zone.isFallback ? (
                  <p className="flex items-start gap-2 text-amber-800 dark:text-amber-300" data-testid="tournament-settings-timezone-fallback">
                    <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      {t('tournamentSettings.timeZone.fallback')}{' '}
                      <Link to="/settings/general-details" className={cn('rounded font-medium underline underline-offset-2', FOCUS_RING)}>{t('tournamentSettings.timeZone.fix')}</Link>
                    </span>
                  </p>
                ) : (
                  <p className="flex items-start gap-2 text-muted-foreground" data-testid="tournament-settings-timezone">
                    <Globe aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{t('tournamentSettings.timeZone.note', { timeZone: zone.id ?? '' })}</span>
                  </p>
                )}
                <p className="flex items-start gap-2 text-muted-foreground" data-testid="tournament-settings-company-disable-note">
                  <BellOff aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {t('tournamentSettings.companyDisable.note')}{' '}
                    <Link to="/settings/notification-management" className={cn('rounded font-medium text-foreground underline underline-offset-2', FOCUS_RING)}>{t('tournamentSettings.companyDisable.link')}</Link>
                  </span>
                </p>
              </div>

              <SettingsSection testId="section-quiet-hours" title={t('tournamentSettings.quiet.title')} description={t('tournamentSettings.quiet.description')}>
                {() => (
                  <>
                    <Switch
                      checked={form.quietHoursEnabled}
                      onCheckedChange={setQuietHoursEnabled}
                      label={t('tournamentSettings.quiet.toggle')}
                      description={t('tournamentSettings.quiet.toggleHelp')}
                      data-testid="quiet-hours-switch"
                    />
                    {form.quietHoursEnabled && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <HourSelect label={t('tournamentSettings.quiet.start')} value={form.quietHoursStartHour} onChange={(hour) => patch({ quietHoursStartHour: hour })} invalid={sameHours} describedBy={sameHours ? windowErrorId : undefined} testId="quiet-hours-start" />
                          <HourSelect label={t('tournamentSettings.quiet.end')} value={form.quietHoursEndHour} onChange={(hour) => patch({ quietHoursEndHour: hour })} invalid={sameHours} describedBy={sameHours ? windowErrorId : undefined} testId="quiet-hours-end" />
                        </div>
                        {sameHours ? (
                          <FieldError id={windowErrorId} testId="quiet-hours-error">{t('tournamentSettings.error.sameHours')}</FieldError>
                        ) : (
                          <p className="text-sm text-muted-foreground" data-testid="quiet-hours-window">
                            {t(form.quietHoursStartHour > form.quietHoursEndHour ? 'tournamentSettings.quiet.windowNextDay' : 'tournamentSettings.quiet.windowSameDay', {
                              start: hourLabel(form.quietHoursStartHour),
                              end: hourLabel(form.quietHoursEndHour),
                            })}
                          </p>
                        )}
                      </>
                    )}
                  </>
                )}
              </SettingsSection>

              <SettingsSection testId="section-hr-summary" title={t('tournamentSettings.summary.title')} description={t('tournamentSettings.summary.description')}>
                {(headingId) => (
                  <>
                    <ChoiceList
                      name="hr-summary-mode"
                      labelledBy={headingId}
                      value={form.hrSummaryMode}
                      onChange={(value) => patch({ hrSummaryMode: value })}
                      options={[
                        { value: HrSummaryMode.Hourly, title: t('tournamentSettings.summary.hourly'), description: t('tournamentSettings.summary.hourlyHelp') },
                        { value: HrSummaryMode.AtTimes, title: t('tournamentSettings.summary.atTimes'), description: t('tournamentSettings.summary.atTimesHelp') },
                      ]}
                    />
                    {form.hrSummaryMode === HrSummaryMode.AtTimes && (
                      <div className="space-y-2">
                        <p id={timesLabelId} className="text-sm font-medium text-foreground">{t('tournamentSettings.summary.timesLabel')}</p>
                        <div role="group" aria-labelledby={timesLabelId} className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
                          {HOURS.map((hour) => {
                            const chosen = form.hrSummaryTimes.includes(hour);
                            return (
                              <button
                                key={hour}
                                type="button"
                                aria-pressed={chosen}
                                data-testid={`summary-time-${hour}`}
                                onClick={() => toggleSummaryHour(hour)}
                                className={cn(
                                  'h-11 cursor-pointer rounded-lg border text-sm font-medium tabular-nums transition-colors duration-200 motion-reduce:transition-none',
                                  FOCUS_RING,
                                  chosen ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-muted/50',
                                )}
                              >
                                {hourLabel(hour)}
                              </button>
                            );
                          })}
                        </div>
                        {timesMissing && attempted ? (
                          <FieldError testId="summary-times-error">{t('tournamentSettings.error.timesRequired')}</FieldError>
                        ) : (
                          <p className="text-sm text-muted-foreground" data-testid="summary-times-count">
                            {timesMissing ? t('tournamentSettings.error.timesRequired') : t('tournamentSettings.summary.timesChosen', { count: form.hrSummaryTimes.length })}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </SettingsSection>

              <SettingsSection testId="section-acceptance-notices" title={t('tournamentSettings.acceptance.title')} description={t('tournamentSettings.acceptance.description')}>
                {(headingId) => (
                  <ChoiceList
                    name="acceptance-notice-mode"
                    labelledBy={headingId}
                    value={form.acceptanceNoticeMode}
                    onChange={(value) => patch({ acceptanceNoticeMode: value })}
                    options={[
                      { value: AcceptanceNoticeMode.SummaryOnly, title: t('tournamentSettings.acceptance.summaryOnly'), description: t('tournamentSettings.acceptance.summaryOnlyHelp') },
                      { value: AcceptanceNoticeMode.InAppPerAcceptance, title: t('tournamentSettings.acceptance.inApp'), description: t('tournamentSettings.acceptance.inAppHelp') },
                      { value: AcceptanceNoticeMode.AllChannels, title: t('tournamentSettings.acceptance.allChannels'), description: t('tournamentSettings.acceptance.allChannelsHelp') },
                    ]}
                  />
                )}
              </SettingsSection>

              <SettingsSection testId="section-expiry-notices" title={t('tournamentSettings.expiry.title')} description={t('tournamentSettings.expiry.description')}>
                {(headingId) => (
                  <ChoiceList
                    name="expiry-notice-mode"
                    labelledBy={headingId}
                    value={form.expiryNoticeMode}
                    onChange={(value) => patch({ expiryNoticeMode: value })}
                    options={[
                      { value: ExpiryNoticeMode.BatchedPerRun, title: t('tournamentSettings.expiry.batched'), description: t('tournamentSettings.expiry.batchedHelp') },
                      { value: ExpiryNoticeMode.PerAssignment, title: t('tournamentSettings.expiry.perAssignment'), description: t('tournamentSettings.expiry.perAssignmentHelp') },
                    ]}
                  />
                )}
              </SettingsSection>
            </div>

            {pageAlert && (
              <div role="alert" data-testid="tournament-settings-save-error" className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-700 dark:text-red-300">{pageAlert}</span>
              </div>
            )}

            {/* The bar and its "saved" note travel as one sticky block. Save is reachable from any
                scroll position, so a note left in the page flow would sit below the fold exactly when
                it is needed. Inside this block the bar's own `sticky` has nowhere to travel and it lays
                out as a plain block; its top margin collapses through, so the spacing is unchanged.
                The note is `absolute` so it never moves the bar; this sticky block is its positioned
                ancestor, which keeps it inside <main> (see the scrolling rule in CLAUDE.md). */}
            <div className="sticky bottom-0">
              {justSaved && (
                <p
                  role="status"
                  data-testid="tournament-settings-saved"
                  className="absolute bottom-full end-0 mb-2 flex max-w-full items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-lg"
                >
                  <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span>{t('tournamentSettings.saved')}</span>
                </p>
              )}
              <StickyActionBar
                hasUnsavedChanges={hasUnsavedChanges}
                isSaving={saving}
                onReset={handleReset}
                onSave={() => { void handleSave(); }}
                t={t}
                saveTestId="tournament-settings-save"
                resetTestId="tournament-settings-reset"
              />
            </div>
          </div>
        )}
      </PageLayout>
    </div>
  );
};

export default TournamentSettings;
